import { useInfiniteQuery, InfiniteData } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { selectActiveCompanyId } from "src/redux/features/user/selectors/userState.selectors";
import { fetchCallHistory } from "../actions/callHistory.actions";
import { CallHistoryListResponse } from "../types";

interface UseInfiniteCallHistoryQueryProps {
  sessionNumber?: string | null;
  limit?: number;
  status?: string;
  direction?: "inbound" | "outbound";
}

type CallHistoryQueryKey = [
  "call-history",
  number | string,
  string,
  number | undefined,
  string | undefined,
  string | undefined,
];

export const useInfiniteCallHistoryQuery = ({
  sessionNumber,
  limit,
  status,
  direction,
}: UseInfiniteCallHistoryQueryProps) => {
  const activeCompanyId = useSelector(selectActiveCompanyId);

  return useInfiniteQuery<
    CallHistoryListResponse,
    Error,
    InfiniteData<CallHistoryListResponse>,
    CallHistoryQueryKey,
    number
  >({
    queryKey: [
      "call-history",
      activeCompanyId ?? "no-company",
      sessionNumber || "no-session",
      limit,
      status,
      direction,
    ],
    queryFn: ({ pageParam }) =>
      fetchCallHistory({
        sessionNumber: sessionNumber || undefined,
        pageParam,
        limit,
        status,
        direction,
      }),

    initialPageParam: 0,

    getNextPageParam: (lastPage, allPages) => {
      const loadedItems = allPages.reduce(
        (acc, page) => acc + page.data.length,
        0
      );

      if (loadedItems >= lastPage.total) {
        return undefined;
      }

      return loadedItems;
    },

    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,

    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,

    enabled: Boolean(activeCompanyId),
  });
};
